import { useMutation, UseMutationResult, useQueryClient } from "@tanstack/react-query";
import { leadListsApi } from "../../../libs/landvoice-api";
import { LeadList } from "../../../types/neighborhoodSearch";
import {
  getNsSavedSearchCacheKey,
  getNsSavedSearchDetailsCacheKey,
  getNsSavedSearchStatusCacheKey,
  NS_LIST_SAVED_SEARCHES_CACHE_KEY,
  NS_INFINITE_LIST_SAVED_SEARCHES_CACHE_KEY,
  NS_SAVED_SEARCH_CACHE_KEY
} from "../constants/cacheKeys";

export const useRenameSavedSearchMutation = (): UseMutationResult<
  LeadList,
  Error,
  { listId: string; newName: string }
> => {
  const queryClient = useQueryClient();

  return useMutation<LeadList, Error, { listId: string; newName: string }>({
    mutationKey: ["NS_RENAME_SAVED_SEARCH_MUTATION"],
    mutationFn: async ({
      listId,
      newName,
    }: {
      listId: string;
      newName: string;
    }) => {
      // Using patchList for partial update - only sending the name field
      const response = await leadListsApi.patchList(listId, {
        name: newName
      } as LeadList);
      return response.data;
    },
    onSuccess: (_, { listId }) => {
      // Invalidate all caches related to this specific search
      queryClient.invalidateQueries({
        queryKey: getNsSavedSearchCacheKey(listId),
      });
      queryClient.invalidateQueries({
        queryKey: getNsSavedSearchDetailsCacheKey(listId),
      });
      queryClient.invalidateQueries({
        queryKey: getNsSavedSearchStatusCacheKey(listId),
      });

      // Invalidate all saved searches lists (both regular and infinite)
      queryClient.invalidateQueries({
        queryKey: [NS_LIST_SAVED_SEARCHES_CACHE_KEY],
      });
      queryClient.invalidateQueries({
        queryKey: [NS_INFINITE_LIST_SAVED_SEARCHES_CACHE_KEY],
      });

      // Invalidate all saved search caches to ensure consistency
      // This will clear any cached instances of this search
      queryClient.invalidateQueries({
        queryKey: [NS_SAVED_SEARCH_CACHE_KEY],
      });
    },
  });
};